<template>
  <c-trigger
    v-model:popup-visible="visible"
    :click-to-close="false"
    preventFocus
    animationName="slide-dynamic-origin"
    :auto-fit-popup-width="
      props.options.length === 0 ? true : props.autoFitPopupWidth
    "
    :disabled="props.disabled || props.readonly"
    @popup-visible-change="(e) => emit('popup-visible-change', e)"
  >
    <a-input
      ref="inputRef"
      class="unit_input"
      :class="{
        [props.customClass]: true,
        unit_input_clear: showClear,
      }"
      v-model="unitInput"
      :readonly="props.readonly"
      :error="props.error"
      :disabled="props.disabled"
      :placeholder="props.placeholder"
      @blur="handleBlur"
      @focus="handleFocus"
    >
      <template #suffix>
        <div class="icon-select">
          <icon-up v-if="visible" />
          <icon-down v-else />
        </div>
        <div class="clear-btn" v-if="showClear" @click.stop="handleClear">
          <icon-close></icon-close>
        </div>
      </template>
    </a-input>
    <template #content>
      <c-unit
        v-model="props.modelValue"
        :class="props.bodyCustomClass"
        :options="props.options"
        :number="props.number"
        :align="props.align"
        :field-names="props.fieldNames"
        @change="handleChangeUnit"
      >
        <template v-if="$slots.footer" #footer>
          <slot name="footer"></slot>
        </template>
      </c-unit>
    </template>
  </c-trigger>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useFormItem } from '@arco-design/web-vue';
import CUnit from './index.vue';

const emit = defineEmits([
  'change',
  'clear',
  'update:modelValue',
  'update:defaultShow',
  'blur',
  'focus',
  'popup-visible-change',
]);
const props = defineProps({
  modelValue: {},
  customClass: String,
  bodyCustomClass: String,
  placeholder: String,
  allowClear: Boolean,
  error: Boolean,
  align: String,
  disabled: Boolean,
  readonly: Boolean,
  // 默认展示字段
  defaultShow: String,
  // 下拉是否和选择器一样的宽度
  autoFitPopupWidth: Boolean,
  options: {
    required: true,
    type: Array,
    default: [],
  },
  number: {
    type: Number,
    default: 4,
  },
  fieldNames: {
    type: Object,
    default: {
      label: 'label',
      value: 'value',
    },
  },
});

const { eventHandlers } = useFormItem();
const visible = ref(false);
const inputRef = ref(null);
const isFocus = ref(false);
const unitInput = ref(props.defaultShow || props.modelValue);
const showClear = computed(
  () =>
    props.allowClear && unitInput.value && !props.disabled && !props.readonly,
);

const handleAssign = () => {
  const obj = props.options.find(
    (ev) => ev[props.fieldNames.value] === props.modelValue,
  );
  unitInput.value = String(
    obj?.[props.fieldNames.label] ||
      props.defaultShow ||
      props.modelValue ||
      '',
  );
};

watch([() => props.modelValue, () => props.options], handleAssign, {
  immediate: true,
});

const handleChangeUnit = (item) => {
  const value = item[props.fieldNames.value];
  visible.value = false;
  emit('update:modelValue', value);
  emit('change', item);
  emit('blur');
  eventHandlers.value?.onChange?.(item);
};

const handleFocus = (ev) => {
  isFocus.value = true;
  emit('focus', ev);
};

const handleBlur = (ev) => {
  isFocus.value = false;
  handleAssign();
  visible.value = false;
  emit('blur');
};

const handleClear = () => {
  unitInput.value = '';
  emit('clear', null);
  emit('update:modelValue', null);
  eventHandlers.value?.onChange?.(null);
};
</script>

<style lang="scss" scoped>
.unit_input {
  &.arco-input-wrapper {
    ::v-deep(.arco-input-suffix) {
      background-color: var(--color_white) !important;
      margin-left: 0 !important;
      border-left: none !important;
      padding: 0 8px !important;
    }
  }
  .clear-btn {
    cursor: pointer;
    font-size: 12px;
    line-height: 12px;
    display: none;
    position: relative;
    .arco-icon {
      position: relative;
    }
    &::before {
      content: '';
      width: 20px;
      height: 20px;
      display: block;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      background-color: transparent;
      border-radius: var(--border_radius_circle);
      transition: background-color 0.1s cubic-bezier(0, 0, 1, 1);
    }
    &:hover::before {
      background-color: var(--color_neutral03);
    }
  }
  &.unit_input_clear:hover {
    .clear-btn {
      display: block;
    }
    .icon-select,
    .icon-search {
      display: none;
    }
  }
}
</style>
